import {
  useGetSheetDataRows,
  useSheetDataRowUtils,
} from "@/domain/SheetDataRow";
import {
  Sheet,
  useRecursiveDeleteSheet,
  useSetSheet,
  useUpdateSheet,
} from "../../../../domain/Sheet";
import {
  useGetSheetFieldSchemas,
  useSetSheetFieldSchema,
} from "../../../../domain/SheetFieldSchema";
import { ManagedTransaction } from "../../../../firebase/firestore";

import { postDeleteAllRows, postInsertRows } from "@/api/sheets";
import { useDeleteReferredFrom } from "@/domain/ReferredFrom";
import { generateId } from "@/utils/generateId";

const duplicateSheetData = async ({
  organizationId,
  source,
  target,
  backend,
  getSheetDataRows,
  sheetDataRowUtils,
}: {
  organizationId: string;
  source: {
    versionId: string;
    sheetId: string;
  };
  target: {
    versionId: string;
    sheetId: string;
  };
  backend?: string;
  getSheetDataRows: ReturnType<typeof useGetSheetDataRows>;
  sheetDataRowUtils: ReturnType<typeof useSheetDataRowUtils>;
}) => {
  const rows = await getSheetDataRows({
    organizationId,
    versionId: source.versionId,
    sheetId: source.sheetId,
  });

  if (rows.length === 0) return;

  if (backend === "lds") {
    await postInsertRows({
      organizationId,
      versionId: target.versionId,
      sheetId: target.sheetId,
      afterRowId: null,
      items: rows.map((r) => ({
        row_id: r.id,
        sheet_data_cells: r.sheetDataCells,
      })),
    });
  } else {
    await sheetDataRowUtils.addRowsByData(
      rows.map((row) => ({
        sheetDataCells: row.sheetDataCells,
      })),
      {
        versionId: target.versionId,
        sheetId: target.sheetId,
      }
    );
  }
};

export const duplicateRecursiveSheetData = async ({
  organizationId,
  versionId,
  sourceSheet,
  sourceVersionId,
  setSheet,
  getSheetFieldSchemas,
  setSheetFieldSchema,
  getSheetDataRows,
  sheetDataRowUtils,
  displayName,
  name,
  sort,
}: {
  sourceSheet: Sheet;
  sourceVersionId: string;
  setSheet: ReturnType<typeof useSetSheet>;
  getSheetFieldSchemas: ReturnType<typeof useGetSheetFieldSchemas>;
  setSheetFieldSchema: ReturnType<typeof useSetSheetFieldSchema>;
  getSheetDataRows: ReturnType<typeof useGetSheetDataRows>;
  sheetDataRowUtils: ReturnType<typeof useSheetDataRowUtils>;
  displayName: Sheet["displayName"];
  name: Sheet["name"];
  sort: number;
  organizationId: string;
  versionId: string;
}) => {
  const sheetFieldSchemas = await getSheetFieldSchemas({
    sheetId: sourceSheet.id,
    versionId: sourceVersionId,
  });

  const newSheetId = generateId();

  // 新規シート作成
  await ManagedTransaction.runTransaction(async (transaction) => {
    await setSheet(
      {
        ...sourceSheet,
        firstRowId: null,
        id: newSheetId,
        displayName,
        name,
        sort,
      },
      {},
      { transaction }
    );

    for (const sheetFieldSchema of sheetFieldSchemas) {
      await setSheetFieldSchema(
        {
          ...sheetFieldSchema,
        },
        { sheetId: newSheetId },
        { transaction }
      );
    }
  });
  await duplicateSheetData({
    organizationId,
    source: {
      versionId: sourceVersionId,
      sheetId: sourceSheet.id,
    },
    target: {
      versionId,
      sheetId: newSheetId,
    },
    backend: sourceSheet.backend,
    getSheetDataRows,
    sheetDataRowUtils,
  });
};

export const deleteRecursiveSheetData = async ({
  sheets,
  selectedSheetIds,
  recursiveDeleteSheet,
  deleteReferredFrom,
  organizationId,
  versionId,
  updateSheet,
  setProgressCount,
}: {
  sheets: Sheet[];
  selectedSheetIds: string[];
  recursiveDeleteSheet: ReturnType<typeof useRecursiveDeleteSheet>;
  deleteReferredFrom: ReturnType<typeof useDeleteReferredFrom>;
  organizationId: string;
  versionId: string;
  updateSheet: ReturnType<typeof useUpdateSheet>;
  setProgressCount: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const restSheets = sheets.filter(({ id }) => !selectedSheetIds.includes(id));
  for (const ldsSheet of sheets.filter(
    (s) => selectedSheetIds.includes(s.id) && s.backend === "lds"
  )) {
    await postDeleteAllRows({
      organizationId,
      versionId,
      sheetId: ldsSheet.id,
    });
  }
  const deleteQueueList = await Promise.all(
    selectedSheetIds.map(
      async (sheetId) => await recursiveDeleteSheet(sheetId, {})
    )
  );
  // 1. referencing sheetがあった場合、referenced sheetのreferred_fromを先に削除
  // 2. 対象のシートを全削除
  // 3. ソート番号を振り直す
  await ManagedTransaction.runTransaction(async (transaction) => {
    await Promise.all(
      sheets
        .filter((s) => selectedSheetIds.includes(s.id) && !!s.referTo)
        .map(
          async (s) =>
            await deleteReferredFrom(
              `${versionId}:${s.id}`,
              {
                versionId: s.referTo!.versionId,
                sheetId: s.referTo!.sheetId,
              },
              { transaction }
            )
        )
    );
    setProgressCount(0);
    for (const deleteQueue of deleteQueueList) {
      await deleteQueue.exec(transaction);
      setProgressCount((cnt) => cnt + 1);
    }
    // NOTE: 既存のドキュメント更新のため、updateDocでOK
    for (const [index, sheet] of restSheets.entries()) {
      await updateSheet(
        { ...sheet, sort: index },
        { sheetId: sheet.id },
        { transaction }
      );
    }
  });
};
